/**
 * Document types section component for sidebar navigation.
 * Displays selectable document type items with badge counts.
 */

import { ClipboardCheck, FileEdit, FileText, Receipt } from "lucide-react";
import type React from "react";
import { useCallback } from "react";
import type { TabType } from "@/lib/url-state";

/** Document type configuration */
interface DocumentTypeConfig {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeColor?: "red" | "purple";
}

/** Document type configurations in display order */
const DOCUMENT_TYPES: DocumentTypeConfig[] = [
  { id: "invoices", label: "All Invoices", icon: FileText },
  { id: "drafts", label: "Pending Drafts", icon: ClipboardCheck, badgeColor: "red" },
  { id: "corrections", label: "Corrections", icon: FileEdit, badgeColor: "purple" },
  { id: "proforma", label: "Pro-Forma", icon: Receipt },
];

interface DocumentTypesSectionProps {
  /** Currently selected document type */
  selectedType: TabType;
  /** Callback when a document type is selected */
  onTypeSelect: (type: TabType) => void;
  /** Badge counts for each document type */
  counts?: Partial<Record<TabType, number>>;
  /** Whether counts are loading */
  isCountsLoading?: boolean;
}

/**
 * Renders a section of document type items for sidebar navigation.
 *
 * @example
 * <DocumentTypesSection
 *   selectedType="invoices"
 *   onTypeSelect={handleSelect}
 *   counts={{ drafts: 5, corrections: 2 }}
 * />
 */
export function DocumentTypesSection({
  selectedType,
  onTypeSelect,
  counts = {},
  isCountsLoading = false,
}: DocumentTypesSectionProps) {
  return (
    <div className="space-y-0.5" data-testid="document-types-section">
      <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Document Types
      </div>
      {DOCUMENT_TYPES.map((docType) => (
        <DocumentTypeItem
          key={docType.id}
          config={docType}
          isSelected={selectedType === docType.id}
          onSelect={() => onTypeSelect(docType.id)}
          count={counts[docType.id]}
          isCountLoading={isCountsLoading}
        />
      ))}
    </div>
  );
}

interface DocumentTypeItemProps {
  config: DocumentTypeConfig;
  isSelected: boolean;
  onSelect: () => void;
  count?: number;
  isCountLoading?: boolean;
}

function DocumentTypeItem({ config, isSelected, onSelect, count, isCountLoading = false }: DocumentTypeItemProps) {
  const { id, label, icon: Icon, badgeColor } = config;

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect();
      }
    },
    [onSelect],
  );

  // Determine badge styling based on color
  const getBadgeClasses = () => {
    if (badgeColor === "red") {
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    }
    if (badgeColor === "purple") {
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
    }
    return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
  };

  return (
    <button
      type="button"
      role="treeitem"
      aria-selected={isSelected}
      aria-level={1}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
        isSelected
          ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
      }`}
      data-testid={`document-type-${id}`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 truncate text-left font-medium">{label}</span>
      {!isCountLoading && count !== undefined && count > 0 && (
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${getBadgeClasses()}`}>{count}</span>
      )}
    </button>
  );
}
